import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";

const STORAGE_KEY = "cb-cookie-consent-v1";

function readConsent() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
  } catch (e) {
    return null;
  }
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Only show once -- depends on the visitor's stored choice.
    const existing = readConsent();
    if (!existing || !existing.choice) {
      // small delay so it doesn't compete with first paint
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
    return undefined;
  }, []);

  const persist = (choice) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ choice, ts: new Date().toISOString() }));
    } catch (e) {
      /* ignore storage errors */
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      aria-live="polite"
      data-testid="cookie-consent-banner"
      className="fixed bottom-4 left-4 right-4 z-[200] mx-auto max-w-md rounded-2xl border border-border bg-white/95 p-5 shadow-panel backdrop-blur-md md:left-6 md:right-auto"
    >
      <button
        type="button"
        onClick={() => persist("essential")}
        aria-label="Dismiss and accept essential cookies only"
        data-testid="cookie-dismiss"
        className="absolute right-3 top-3 flex h-7 w-7 items-center justify-center rounded-full text-cbslate transition-colors hover:bg-cbmist hover:text-cbink"
      >
        <X size={16} />
      </button>

      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-cbblue-soft text-primary">
          <Cookie size={20} />
        </span>
        <div className="min-w-0">
          <h3 className="text-sm font-bold text-cbink">We value your privacy</h3>
          <p className="mt-1.5 text-[13px] leading-relaxed text-cbslate">
            We use cookies to run essential features and, with your consent, to improve your experience.
            Your choice is remembered, so you&rsquo;ll only see this once. Read our{" "}
            <Link to="/cookie-policy" className="font-semibold text-primary hover:underline">Cookie Policy</Link>.
          </p>
        </div>
      </div>

      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
        <button
          type="button"
          onClick={() => persist("all")}
          data-testid="cookie-accept-all"
          className="btn-primary w-full justify-center sm:flex-1"
        >
          Accept all
        </button>
        <button
          type="button"
          onClick={() => persist("essential")}
          data-testid="cookie-essential-only"
          className="btn-secondary w-full justify-center sm:flex-1"
        >
          Essential only
        </button>
      </div>
    </div>
  );
}
